// EventCalendar.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MdOutlineCalendarMonth } from "react-icons/md";

const EventCalendar = ({ 
  filteredCases, 
  loading, 
  userType, 
  selectedLabId, 
  selectedLabName, 
  userName,
  onEventClick 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [allEvents, setAllEvents] = useState([]);
  const eventsContainerRef = useRef(null);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  const getCountsForDate = useCallback((date) => {
    if (loading || !Array.isArray(filteredCases) || filteredCases.length === 0) {
      return {
        newCases: 0,
        arrive: 0,
        ship: 0,
        tasks: 0,
        deliveries: 0
      };
    }
    
    const dateString = formatDateToYYYYMMDD(date);
    if (!dateString) return { newCases: 0, arrive: 0, ship: 0, tasks: 0, deliveries: 0 };
    
    const newCasesCount = filteredCases.filter(caseItem => {
      if (!caseItem) return false;
      const caseDate = caseItem.date || caseItem.start_date;
      if (!caseDate) return false;
      const caseDateString = formatDateToYYYYMMDD(caseDate);
      return caseDateString === dateString;
    }).length;
    
    const arriveCount = filteredCases.filter(caseItem => {
      if (!caseItem || !caseItem.arrive_date) return false;
      const arriveDateString = formatDateToYYYYMMDD(caseItem.arrive_date);
      return arriveDateString === dateString;
    }).length;
    
    const shipCount = filteredCases.filter(caseItem => {
      if (!caseItem || !caseItem.delivery_date) return false;
      const shipDateString = formatDateToYYYYMMDD(caseItem.delivery_date);
      return shipDateString === dateString;
    }).length;
    
    const deliveriesCount = filteredCases.filter(caseItem => {
      if (!caseItem || !caseItem.delivery_date) return false;
      const deliveryDateString = formatDateToYYYYMMDD(caseItem.delivery_date);
      return deliveryDateString === dateString;
    }).length;
    
    return {
      newCases: newCasesCount,
      arrive: arriveCount,
      ship: shipCount,
      deliveries: deliveriesCount,
      tasks: 0
    };
  }, [filteredCases, loading]);

  const generateEventsForCurrentMonth = useCallback(() => {
    const events = [];
    const today = new Date();
    const todayDateString = today.toDateString();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const monthName = date.toLocaleDateString('default', { month: 'short' });
      const isSelectedDate = date.toDateString() === selectedDate.toDateString();

      const counts = getCountsForDate(date);
      
      events.push({
        date: `${dayName} ${monthName} ${day} ${currentYear}`,
        newCases: counts.newCases,
        arrive: counts.arrive,
        ship: counts.ship,
        tasks: counts.tasks,
        deliveries: counts.deliveries,
        fullDate: date,
        isToday: date.toDateString() === todayDateString,
        isSelected: isSelectedDate,
        dayOfMonth: day,
        dateString: formatDateToYYYYMMDD(date)
      });
    }
    
    return events;
  }, [currentMonth, currentYear, selectedDate, getCountsForDate]);

  useEffect(() => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  }, []);

  useEffect(() => {
    setAllEvents(generateEventsForCurrentMonth());
  }, [generateEventsForCurrentMonth]);

  const eventsPerView = isMobile ? 1 : isTablet ? 2 : 4;
  const maxIndex = Math.max(0, allEvents.length - eventsPerView);

  useEffect(() => {
    const selectedIndex = allEvents.findIndex(e => e.isSelected);
    if (selectedIndex !== -1) {
      setCurrentEventIndex(Math.max(0, Math.min(selectedIndex - 1, maxIndex)));
    }
  }, [allEvents, maxIndex]);

  const visibleEvents = allEvents.slice(currentEventIndex, currentEventIndex + eventsPerView);
  
  const nextEvent = () => setCurrentEventIndex(p => p >= maxIndex ? 0 : p + 1);
  const prevEvent = () => setCurrentEventIndex(p => p === 0 ? maxIndex : p - 1);

  const handleEventCardClick = (day) => {
    setSelectedDate(day.fullDate);
    if (onEventClick) {
      onEventClick(day, 'Day Overview');
    }
  };

  return (
    <div style={{ marginBottom: isMobile ? '1.5rem' : '3rem', position: 'relative' }}>
      <div
        style={{
          backgroundColor: '#004aad',
          color: 'white',
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: '18px',
          fontWeight: 'bold',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          boxSizing: 'border-box',
          borderRadius: '8px 8px 0 0'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '22px' }}><MdOutlineCalendarMonth /></span>
          <span>
            EVENTS 
            {userType === 'lab' ? (
              selectedLabId === 'all' ? ' (All Labs)' : ` (${selectedLabName || selectedLabId})`
            ) : userType !== 'admin' ? ` (${userName})` : ''}
          </span>
        </div>
        
        <div style={{
          fontSize: '0.85rem',
          color: '#cbd5e1',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {loading && <span>Loading events...</span>}
          {!loading && (
            <span>
              Showing {filteredCases.length} {selectedLabId === 'all' ? 'total' : userType === 'lab' ? 'lab' : userType !== 'admin' ? 'user' : ''} cases
            </span>
          )}
        </div>
      </div>

      <div style={{
        background: 'transparent',
        borderRadius: '0 12px 12px 12px',
        padding: isMobile ? '1rem' : '2rem 3rem',
        position: 'relative',
        overflow: 'hidden'
      }} ref={eventsContainerRef}>
        <button onClick={prevEvent} style={{
          position: 'absolute',
          left: isMobile ? '5px' : 0,
          top: '50%',
          transform: 'translateY(-50%)',
          background: currentEventIndex === 0 ? '#cbd5e1' : '#004aad',
          color: 'white',
          border: 'none',
          width: isMobile ? '30px' : '36px',
          height: isMobile ? '30px' : '36px',
          borderRadius: '50%',
          cursor: currentEventIndex === 0 ? 'not-allowed' : 'pointer',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ChevronLeft size={isMobile ? 14 : 18} />
        </button>
        <button onClick={nextEvent} style={{
          position: 'absolute',
          right: isMobile ? '5px' : 0,
          top: '50%',
          transform: 'translateY(-50%)',
          background: currentEventIndex >= maxIndex ? '#cbd5e1' : '#004aad',
          color: 'white',
          border: 'none',
          width: isMobile ? '30px' : '36px',
          height: isMobile ? '30px' : '36px',
          borderRadius: '50%',
          cursor: currentEventIndex >= maxIndex ? 'not-allowed' : 'pointer',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ChevronRight size={isMobile ? 14 : 18} />
        </button>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? '1rem' : '1.5rem',
          overflow: 'visible'
        }}>
          {visibleEvents.map((day) => (
            <div
              key={`${day.dayOfMonth}-${currentMonth}`}
              style={{
                background: day.isToday ? '#f0f9ff' : day.isSelected ? '#e6f7ff' : '#f8fafc',
                borderRadius: '12px',
                padding: isMobile ? '1rem' : '1.5rem',
                border: day.isToday || day.isSelected ? '2px solid #004aad' : '1px solid #e2e8f0',
                position: 'relative',
                transition: 'all 0.2s ease',
                minHeight: isMobile ? 'auto' : '240px',
                cursor: 'pointer'
              }}
              onClick={() => handleEventCardClick(day)}
            >
              {(day.isToday || day.isSelected) && (
                <div style={{
                  position: 'absolute',
                  top: '-25px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#004aad',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: '800',
                  padding: '0.6rem 2rem',
                  borderRadius: '20px',
                  whiteSpace: 'nowrap',
                  minWidth: '100px',
                  textAlign: 'center',
                  zIndex: 5,
                  pointerEvents: 'none',
                }}>
                  {day.isToday ? 'TODAY' : 'SELECTED'}
                </div>
              )}
              <div style={{
                fontWeight: '700',
                color: '#004aad',
                textAlign: 'center',
                marginBottom: '1rem',
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}>{day.date}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {['New Cases', 'Arrive', 'Ship', 'Tasks', 'Deliveries'].map((label) => (
                  <div 
                    key={label} 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      cursor: 'pointer', 
                      padding: '4px', 
                      borderRadius: '4px' 
                    }} 
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f9ff'} 
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span style={{ color: '#64748b', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>{label}:</span>
                    <span style={{ 
                      fontWeight: '700', 
                      color: day[label.toLowerCase().replace(' ', '')] > 0 ? '#16a34a' : '#64748b', 
                      fontSize: isMobile ? '0.8rem' : '0.9rem' 
                    }}>
                      {day[label.toLowerCase().replace(' ', '')]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EventCalendar;